import { useCallback, useState } from 'react'
import {
  Button,
  EmptyState,
  Flex,
  SavedRepliesIcon,
  PlusIcon,
  Skeleton,
  CriticalIcon,
} from '@buffer-mono/popcorn'

import { NewSettingsPageLayout } from './components/NewSettingsPageLayout'
import { useSavedReplies } from '../Comments/hooks/savedReplies/useSavedReplies'

import { SavedRepliesList } from './components/SavedRepliesList'
import { SavedReplyForm } from '../Comments/SavedReplyForm/SavedReplyForm'
import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { SavedRepliesPaywall } from '../Comments/components/SavedRepliesPaywall/SavedRepliesPaywall'

export const SavedRepliesSettings = (): JSX.Element => {
  const { savedReplies, loading, error } = useSavedReplies()
  const hasSavedReplies = savedReplies && savedReplies.length > 0
  const [openForm, setOpenForm] = useState(false)
  const [openPaywall, setOpenPaywall] = useState(false)
  const limits = useCurrentOrganization()?.limits

  const hasReachedLimit =
    (savedReplies?.length ?? 0) >= (limits?.savedReplies ?? 0)

  // Skeleton loading view
  const skeletonLoading = loading
  const skeletonLoadingView = [0, 1, 2].map((index) => (
    <Skeleton key={index} width="100%" height="72px" />
  ))

  const handleAddNewSavedReply = useCallback(() => {
    if (hasReachedLimit) {
      setOpenPaywall(true)
    } else {
      setOpenForm(true)
    }
  }, [hasReachedLimit, setOpenPaywall, setOpenForm])
  /**
   * Button to show the form to add a new saved reply
   */
  const AddSavedReplyButton = (
    <Button variant="primary" size="large" onClick={handleAddNewSavedReply}>
      <PlusIcon /> New Saved Reply
    </Button>
  )

  // Error loading view
  const errorLoadingView = (
    <EmptyState variant="critical">
      <EmptyState.Icon>
        <CriticalIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Failed to load saved replies</EmptyState.Heading>
      <EmptyState.Description>
        Something went wrong. Please try again later.
      </EmptyState.Description>
    </EmptyState>
  )

  // Empty state view
  const showEmptyState =
    !loading && !error && !openForm && savedReplies && savedReplies.length === 0

  const emptyStateView = (
    <EmptyState size="xlarge">
      <EmptyState.Icon>
        <SavedRepliesIcon />
      </EmptyState.Icon>
      <EmptyState.Description>
        Save information you share often to quickly insert it to your replies.
      </EmptyState.Description>
      <EmptyState.Actions>{AddSavedReplyButton}</EmptyState.Actions>
    </EmptyState>
  )

  // Saved replies view
  const showSavedReplies =
    !loading && !error && savedReplies && savedReplies.length > 0

  const savedRepliesView = showSavedReplies && (
    <Flex gap="lg" direction="column" fullWidth fullHeight>
      <SavedRepliesList savedReplies={savedReplies} />
    </Flex>
  )

  return (
    <NewSettingsPageLayout
      title="Saved Replies"
      subtitle="Saved Replies are visible to everyone in your organization."
      actions={hasSavedReplies ? AddSavedReplyButton : undefined}
    >
      {skeletonLoading && skeletonLoadingView}
      {openForm && (
        <SavedReplyForm edit={false} open={openForm} setOpen={setOpenForm} />
      )}
      {showSavedReplies && savedRepliesView}
      {showEmptyState && emptyStateView}
      {error && errorLoadingView}

      <SavedRepliesPaywall
        open={openPaywall}
        setOpen={setOpenPaywall}
        upgradePathName="settings-saved-replies-paywall"
        ctaProperties={{
          view: 'settings',
          location: 'saved-replies',
          button: 'upgrade',
        }}
      />
    </NewSettingsPageLayout>
  )
}

export default SavedRepliesSettings
